import { Button, RadioButton } from '@pmi/dsm-react';
import React, { useEffect, useState } from 'react';
import styles from './settings.module.scss';
import { PMIFormatDate } from '../../shared/helpers';
import AccountDto from '../../shared/models/AccountModel';
import { ConsentQueryResult } from './models';

export interface ConsentOptionalProps {
  accountData: AccountDto;
  consentData: ConsentQueryResult;

  handleUpdateConsent: (consentFlag: boolean) => void;
}

export default function ConsentOptional({
  accountData,
  consentData,
  handleUpdateConsent,
}: ConsentOptionalProps) {
  const [consentFlag, setConsentFlag] = useState('allow');
  const onConsentChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setConsentFlag(event.target.value);
  useEffect(() => {
    setConsentFlag(consentData?.share ? 'allow' : 'disallow');
  }, [consentData]);

  return (
    <div className={styles.consentShareCard}>
      <div className="p-4">
        <div className="pb-3 d-flex align-items-center gap-2">
          <div>
            <img
              className={styles.logo}
              alt=""
              src="https://www.pmi.org/assets/img/pmi-adp-logo.svg"
            />
          </div>
          <div>
            <img className={styles.logo} alt="" src={accountData?.logoUrl} />
          </div>
        </div>
        <div className={`pb-3 ${styles.headerSection}`}>
          Agree to share data with {accountData?.name}
        </div>
        <div className={`pb-3 ${styles.subheaderSection}`}>
          <p className="mb-3">
            The benefit of sharing your product data is that it provides{' '}
            {accountData?.name} visibility and transparency of your progress to
            better serve your needs and achieve your goals. You can opt out
            anytime in the Settings page of the Partner Platform.
          </p>
          <p className="mb-3">
            <strong>Always shared:</strong> Name, email address, country,
            product(s) redeemed, date redeemed, and order ID with Organization
            in order to comply with various regulations and the contractual
            relationship between PMI and the Organization.
          </p>
        </div>
        <div
          className={`pb-3 ${styles.consentOptionCheckbox} ${
            consentFlag === 'allow' ? styles.consentOptionCheckboxActive : ''
          }`}
        >
          <RadioButton
            checked={consentFlag === 'allow'}
            onChange={onConsentChange}
            value="allow"
            label={
              <span className={styles.checkboxContainer}>
                <label htmlFor="consent-allow">Share my Product Data</label>
                <p className="d-block">
                  I agree to share my Product Data including exam result, exam
                  date, status, progress and completion with {accountData?.name}
                  .
                </p>
              </span>
            }
            id="consent-allow"
            name="consent"
          />
        </div>
        <div
          className={`pb-3 ${styles.consentOptionCheckbox} ${
            consentFlag === 'disallow' ? styles.consentOptionCheckboxActive : ''
          }`}
        >
          <RadioButton
            checked={consentFlag === 'disallow'}
            onChange={onConsentChange}
            value="disallow"
            label={
              <span className={styles.checkboxContainer}>
                <label htmlFor="consent-disallow">
                  Don&apos;t share my Product Data
                </label>
                <p className="d-block">
                  Only the required data below is shared.
                </p>
              </span>
            }
            id="consent-disallow"
            name="consent"
          />
        </div>
        <div className="pb-3">
          <Button
            disabled={(consentFlag === 'allow') === !!consentData.share}
            onClick={() => handleUpdateConsent(consentFlag === 'allow')}
            titleText="Update consent"
            variant="primary"
          />
        </div>
        <div
          className={`${styles.lastConsentDetails} pb-3`}
          data-testid="consent-details"
        >
          <div>
            <strong>Last Consent:</strong>
          </div>
          <div data-testid="consent-name" data-exclude-lg>
            {consentData?.name}
          </div>
          <div data-testid="consent-email" data-exclude-lg>
            {consentData?.email}
          </div>
          <div>{PMIFormatDate(consentData?.lastUpdated)}</div>
        </div>
        <div className={styles.footerSection}>
          <em>
            You have the ability to change your consent for sharing your
            information at any time. If you choose to opt out, your data will be
            anonymized to {accountData?.name}.
          </em>
        </div>
      </div>
    </div>
  );
}
